/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { CalendarIcon } from '@/shared/ui/radix-icons';
import { Text, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import type { GoogleConnectionState } from '@features/agenda/utils/google-connection';
import { GoogleMark } from './GoogleMark';

/** The two sections of the calendar settings. */
export type SettingsTab = 'calendars' | 'google';

type Translate = (key: string, vars?: Record<string, string>) => string;

export interface CalendarSettingsNavProps {
  active: SettingsTab;
  calendarCount: number;
  googleState: GoogleConnectionState;
  t: Translate;
  onChange: (tab: SettingsTab) => void;
}

/** Dot colour per link state, read at a glance from the navigation. */
const DOT_COLOR: Record<GoogleConnectionState, string> = {
  connected: 'var(--status-success-text)',
  readonly: 'var(--status-warning-text)',
  error: 'var(--status-error-text)',
  disconnected: 'var(--border-medium)',
};

const ITEM_BASE = cn(
  'flex w-full items-center gap-(--space-2) rounded-(--radius-md)',
  'px-(--space-3) py-(--space-2) text-left',
  'font-[family-name:var(--font-family)] text-[length:var(--text-body-sm)]',
  'font-[weight:var(--font-medium)]',
  'transition-colors',
);

/**
 * One navigation entry, with room on the right for a count or a status dot.
 *
 * @param props - Entry state, label, icon and trailing slot.
 * @returns The rendered entry.
 */
const NavItem: React.FC<{
  active: boolean;
  label: string;
  icon: React.ReactNode;
  trailing?: React.ReactNode;
  onClick: () => void;
}> = ({ active, label, icon, trailing, onClick }) => (
  <UnstyledButton
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={cn(
      ITEM_BASE,
      active
        ? 'bg-[var(--surface-raised)] text-[var(--text-primary)] font-[weight:var(--font-semibold)]'
        : 'text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]',
    )}
  >
    {icon}
    <Text component="span" className="flex-1 truncate">
      {label}
    </Text>
    {trailing}
  </UnstyledButton>
);

/**
 * Section navigation for the calendar settings.
 *
 * On desktop it is a column that holds its place while the panel beside it
 * scrolls; on phones there is no room for a column, so the same two entries
 * run across the top as segments. Both render from one piece of state, so the
 * selected section survives a rotation.
 *
 * @param props - Active tab, counters and handlers.
 * @returns The rendered navigation.
 */
export const CalendarSettingsNav: React.FC<CalendarSettingsNavProps> = ({
  active,
  calendarCount,
  googleState,
  t,
  onChange,
}) => (
  <nav
    role="tablist"
    aria-label={t('calendar_settings.sections_aria')}
    className={cn(
      'flex shrink-0 gap-(--space-1)',
      'flex-row border-b border-[var(--border-subtle)] p-(--space-3)',
      'md:w-[var(--settings-nav-width)] md:flex-col md:border-b-0',
      'md:border-r md:bg-[var(--surface-card)] md:p-(--space-3)',
    )}
  >
    <NavItem
      active={active === 'calendars'}
      label={t('calendar_settings.nav_calendars')}
      icon={<CalendarIcon width={15} height={15} aria-hidden="true" />}
      onClick={() => onChange('calendars')}
      trailing={
        <Text
          component="span"
          className="shrink-0 text-[length:var(--text-meta)] font-[weight:var(--font-medium)] text-[var(--text-tertiary-soft)]"
        >
          {calendarCount}
        </Text>
      }
    />
    <NavItem
      active={active === 'google'}
      label={t('calendar_settings.nav_google')}
      icon={<GoogleMark size={15} />}
      onClick={() => onChange('google')}
      trailing={
        <Text
          component="span"
          aria-hidden="true"
          className="block h-2 w-2 shrink-0 rounded-full"
          style={{ background: DOT_COLOR[googleState] }}
        />
      }
    />
  </nav>
);
